import { create } from 'zustand';
import { Infrastructure, InfragraphInfrastructure, VisualizationNode, VisualizationLink, ViewportState, ZoomLevel } from '../types/infrastructure';
import { InfragraphParser } from '../utils/infragraphParser';
import { serialize, parse } from '../utils/serialization';

/**
 * UTF-8 safe string hashing for content comparison
 * Uses TextEncoder to handle Unicode characters properly
 */
function hashString(str: string): string {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) - hash) + data[i];
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

interface VisualizationState {
  // Data
  infrastructure: Infrastructure | null;
  nodes: VisualizationNode[];
  links: VisualizationLink[];
  zoomLevels: ZoomLevel[];

  // Viewport state
  viewport: ViewportState;
  viewportCache: Map<string, ViewportState>; // Store viewport per context

  // Hierarchical context
  currentContext: string | null; // ID of the container we're currently viewing
  contextPath: Array<{id: string, name: string, type: string}>; // Breadcrumb path

  // UI state
  selectedNode: string | null;
  hoveredNode: string | null;
  isLoading: boolean;
  error: string | null;

  // Filters
  showConnectionTypes: string[];
  showNodeTypes: string[];

  // Layout options
  autoLayoutType: 'auto' | 'hierarchical' | 'ring' | 'dragonfly';
  renderedPositions: Map<string, { x: number; y: number }>; // Last rendered positions from the canvas
  highlightFixedNodes: boolean; // Highlight nodes with fixed positions in a different color
  isSimulationRunning: boolean; // Track if force simulation is currently running

  // Editor state
  editorVisible: boolean;
  editorContent: string | null;
  editorFormat: 'yaml' | 'json';
  editorDirty: boolean;
  editorWidth: number;
  lastSyncedHash: string | null;
  editorHighlights: {
    nodeTokens: string[];
    linkTokens: string[];
  };
  editorHighlightEnabled: boolean;

  // Actions
  loadInfrastructure: (data: Infrastructure) => void;
  setViewport: (viewport: Partial<ViewportState>) => void;
  selectNode: (nodeId: string | null) => void;
  hoverNode: (nodeId: string | null) => void;
  zoomToLevel: (level: number) => void;
  zoomToNode: (nodeId: string) => void;
  drillDown: (nodeId: string) => void;
  setContext: (contextId: string | null) => void;
  setFilters: (nodeTypes: string[], connectionTypes: string[]) => void;
  setAutoLayoutType: (type: 'auto' | 'hierarchical' | 'ring' | 'dragonfly') => void;
  setRenderedPositions: (positions: Map<string, { x: number; y: number }>) => void;
  setHighlightFixedNodes: (highlight: boolean) => void;
  setSimulationRunning: (isRunning: boolean) => void;
  reset: () => void;

  // Editor actions
  toggleEditor: () => void;
  setEditorWidth: (width: number) => void;
  setEditorFormat: (format: 'yaml' | 'json') => void;
  updateEditorContent: (content: string) => void;
  syncToEditor: () => void;
  applyEditorChanges: () => void;
  revertEditorChanges: () => void;
  setEditorHighlights: (nodeTokens: string[], linkTokens: string[]) => void;
  setEditorHighlightEnabled: (enabled: boolean) => void;

  // Manual layout actions
  updateNodeLayout: (nodeId: string, svgX: number, svgY: number, capturedPositions?: Map<string, {x: number, y: number}> | null) => void;
  fixAllVisibleNodes: () => void;
  releaseAllVisibleNodes: () => void;
}

/**
 * Calculate the bounding box center of nodes with fixed positions
 */
const calculateNodesBoundingBoxCenter = (nodes: VisualizationNode[]): { x: number; y: number } | null => {
  const fixedNodes = nodes.filter(n => n.fx !== undefined && n.fy !== undefined);
  if (fixedNodes.length === 0) return null;

  const xs = fixedNodes.map(n => n.fx!);
  const ys = fixedNodes.map(n => n.fy!);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2
  };
};

// Initial viewport centers the view on (0,0) in manual layout coordinate space
// Manual layout uses a 2000x2000 reference space with (0,0) at center (1000, 1000)
// To center this in the viewport, we translate by (viewport_center - layoutCenter)
// For automatic layouts, viewport starts at (0, 0)
const getInitialViewport = (hasManualLayout: boolean = false, nodesBoundingBoxCenter?: { x: number; y: number } | null): ViewportState => {
  if (!hasManualLayout) {
    // Automatic layout - no offset needed, force simulation will center it
    return {
      x: 0,
      y: 0,
      zoom: 1,
      selectedNode: undefined
    };
  }

  if (typeof window !== 'undefined') {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    // Use actual bounding box center if available, otherwise assume reference space center
    const layoutCenterX = nodesBoundingBoxCenter?.x ?? 1000;
    const layoutCenterY = nodesBoundingBoxCenter?.y ?? 1000;

    return {
      x: centerX - layoutCenterX,
      y: centerY - layoutCenterY,
      zoom: 1,
      selectedNode: undefined
    };
  }
  return {
    x: 0,
    y: 0,
    zoom: 1,
    selectedNode: undefined
  };
};

const initialViewport: ViewportState = getInitialViewport();

export const useVisualizationStore = create<VisualizationState>((set, get) => ({
  // Initial state
  infrastructure: null,
  nodes: [],
  links: [],
  zoomLevels: [],
  viewport: initialViewport,
  viewportCache: new Map(),
  currentContext: null,
  contextPath: [],
  selectedNode: null,
  hoveredNode: null,
  isLoading: false,
  error: null,
  showConnectionTypes: [],
  showNodeTypes: ['site', 'pod', 'network', 'rack', 'device', 'component'],
  autoLayoutType: 'auto',
  renderedPositions: new Map(),
  highlightFixedNodes: true,
  isSimulationRunning: false,

  // Editor state
  editorVisible: false,
  editorContent: null,
  editorFormat: 'yaml',
  editorDirty: false,
  editorWidth: 500,
  lastSyncedHash: null,
  editorHighlights: {
    nodeTokens: [],
    linkTokens: []
  },
  editorHighlightEnabled: true,

  // Actions
  loadInfrastructure: (data: Infrastructure) => {
    set({ isLoading: true, error: null });

    try {
      // Cast to InfragraphInfrastructure - this assumes the data is in the new format
      // Legacy format is no longer supported by the new parser
      const parser = new InfragraphParser();
      parser.parse(data as InfragraphInfrastructure);

      const nodes = parser.generateVisualizationNodes();
      const links = parser.generateVisualizationLinks();
      const zoomLevels = parser.getZoomLevels();
      const initialContext = parser.getInitialContext();

      // Set initial connection types based on available links
      const connectionTypes = Array.from(new Set(links.map(link => link.protocol).filter(Boolean))) as string[];

      // Detect if this topology uses manual layout (nodes have fixed positions)
      const hasManualLayout = nodes.some(node => node.fx !== undefined || node.fy !== undefined);

      // Calculate bounding box center for manual layouts to properly center viewport
      const boundingBoxCenter = hasManualLayout ? calculateNodesBoundingBoxCenter(nodes) : null;

      set({
        infrastructure: data,
        nodes,
        links,
        zoomLevels,
        showConnectionTypes: connectionTypes,
        currentContext: initialContext,
        contextPath: [],
        isLoading: false,
        error: null,
        viewport: getInitialViewport(hasManualLayout, boundingBoxCenter), // Recalculate with current window size
        viewportCache: new Map(), // Clear viewport cache when loading new infrastructure
        renderedPositions: new Map() // Clear rendered positions snapshot
      });

      // If we have an initial context, build the context path
      if (initialContext) {
        get().setContext(initialContext);
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load infrastructure'
      });
    }
  },

  setViewport: (newViewport: Partial<ViewportState>) => {
    const currentViewport = get().viewport;
    const currentContext = get().currentContext;
    const nodes = get().nodes;
    const viewportCache = get().viewportCache;

    const updatedViewport = { ...currentViewport, ...newViewport };

    // Determine cache key: for device contexts, use device TYPE not instance ID
    // This ensures all instances of the same device type share viewport state
    // Only cache viewport for manual layout (nodes with fixed positions)
    let cacheKey = currentContext;
    if (currentContext !== null) {
      const contextNode = nodes.find(n => n.id === currentContext);

      // Check if current context has manual layout (nodes with fixed positions)
      const contextNodes = currentContext
        ? nodes.filter(n => n.parent === currentContext || n.id === currentContext)
        : nodes;
      const hasManualLayout = contextNodes.some(n => n.fx !== undefined || n.fy !== undefined);

      // Only cache viewport for manual layout topologies
      if (hasManualLayout) {
        if (contextNode && contextNode.type === 'device' && contextNode.metadata?.deviceType) {
          // Use device type, not instance ID, so all instances share the same viewport
          cacheKey = `device:${contextNode.metadata.deviceType}`;
        }

        if (cacheKey !== null) {
          viewportCache.set(cacheKey, updatedViewport);
        }
      }
    }

    set({ viewport: updatedViewport });
  },

  selectNode: (nodeId: string | null) => {
    set({ selectedNode: nodeId });
    // Don't automatically center viewport on selected node
    // This was causing issues when drilling down because node positions
    // haven't been recalculated yet for the new context
  },

  hoverNode: (nodeId: string | null) => {
    set({ hoveredNode: nodeId });
  },

  zoomToLevel: (level: number) => {
    // Only change zoom scale, don't change context or hierarchy
    const zoomLevels = get().zoomLevels;
    if (level >= 0 && level < zoomLevels.length) {
      const targetLevel = zoomLevels[level];
      const zoom = (targetLevel.minZoom + targetLevel.maxZoom) / 2;

      get().setViewport({
        zoom
      });
    }
  },

  zoomToNode: (nodeId: string) => {
    const nodes = get().nodes;
    const node = nodes.find(n => n.id === nodeId);

    if (node) {
      // Just center on the node without changing context or zoom level
      get().setViewport({
        x: -node.x + window.innerWidth / 2,
        y: -node.y + window.innerHeight / 2
      });

      get().selectNode(nodeId);
    }
  },

  drillDown: (nodeId: string) => {
    const nodes = get().nodes;
    const node = nodes.find(n => n.id === nodeId);

    if (node && ['site', 'pod', 'network', 'rack', 'device'].includes(node.type)) {
      get().setContext(nodeId);
      get().selectNode(nodeId);
    }
  },

  setContext: (contextId: string | null) => {
    const nodes = get().nodes;
    const viewportCache = get().viewportCache;
    let contextPath: Array<{id: string, name: string, type: string}> = [];

    if (contextId) {
      // Build context path from root to current context
      let currentNode = nodes.find(n => n.id === contextId);
      const path = [];

      while (currentNode) {
        const nodeForPath = currentNode; // Copy to avoid closure issues
        path.unshift({
          id: nodeForPath.id,
          name: nodeForPath.name,
          type: nodeForPath.type
        });

        if (currentNode?.parent) {
          const parentId = currentNode.parent;
          const parentNode = nodes.find(n => n.id === parentId);
          if (!parentNode) break;
          currentNode = parentNode;
        } else {
          break;
        }
      }

      contextPath = path;
    }

    // Check if current context has manual layout (nodes with fixed positions)
    // Use the same filtering logic as VisualizationEngine to include descendant nodes
    const contextNode = contextId !== null ? nodes.find(n => n.id === contextId) : null;
    const isNetworkContext = contextNode?.type === 'network';

    const contextNodes = contextId
      ? nodes.filter(n => {
          // Direct children
          if (n.parent === contextId || n.id === contextId) {
            return true;
          }
          // For network contexts, include devices whose racks belong to this network
          if (isNetworkContext && n.type === 'device') {
            const deviceRackParent = n.parent;
            const rackNode = nodes.find(rack => rack.id === deviceRackParent);
            if (rackNode && rackNode.parent === contextId) {
              return true;
            }
          }
          return false;
        })
      : nodes;
    const hasManualLayout = contextNodes.some(n => n.fx !== undefined || n.fy !== undefined);

    // Determine if this manual layout needs the centering offset
    // Manual layouts from YAML use the 2000x2000 reference space centered at (1000, 1000)
    // and need the centering offset to appear centered on screen.
    // This applies to BOTH infrastructure-level and component-level YAML layouts.
    // Only captured positions from drag operations (not from YAML) would skip the offset,
    // but those aren't implemented yet.
    const needsCenteringOffset = hasManualLayout; // All YAML manual layouts need centering

    // Always check for cached viewport to preserve view when navigating between levels
    // For device contexts, use device TYPE not instance ID to share viewport across instances
    let cacheKey = contextId;
    if (contextNode && contextNode.type === 'device' && contextNode.metadata?.deviceType) {
      // Use device type, not instance ID, so all instances share the same viewport
      cacheKey = `device:${contextNode.metadata.deviceType}`;
    }

    const cachedViewport = cacheKey !== null ? viewportCache.get(cacheKey) : undefined;

    // Get current viewport and context to check if we're navigating between levels
    const currentViewport = get().viewport;
    const currentContext = get().currentContext;

    // Check if we're navigating FROM a device context (component level)
    // Device contexts have a special viewport behavior (no centering offset)
    const currentContextNode = currentContext !== null ? nodes.find(n => n.id === currentContext) : null;
    // const isNavigatingFromDevice = currentContextNode?.type === 'device'; // For future use

    // Check if the source context had manual layout
    const currentContextNodes = currentContext
      ? nodes.filter(n => {
          if (n.parent === currentContext || n.id === currentContext) {
            return true;
          }
          const isCurrentNetworkContext = currentContextNode?.type === 'network';
          if (isCurrentNetworkContext && n.type === 'device') {
            const deviceRackParent = n.parent;
            const rackNode = nodes.find(rack => rack.id === deviceRackParent);
            if (rackNode && rackNode.parent === currentContext) {
              return true;
            }
          }
          return false;
        })
      : nodes;
    const currentHasManualLayout = currentContextNodes.some(n => n.fx !== undefined || n.fy !== undefined);

    // If we're changing context (navigating) and don't have a cached viewport,
    // preserve the current viewport to avoid jumps ONLY when navigating between manual layout contexts.
    // For autolayout contexts, always reset to identity to avoid the jumpiness
    // that the code comments warn about.
    // Note: We preserve viewport even when at (0,0) because manual layouts can be centered at origin
    const isNavigatingBetweenLevels = hasManualLayout &&
                                      currentHasManualLayout &&
                                      currentContext !== null &&
                                      currentContext !== contextId &&
                                      currentViewport !== undefined;

    // Calculate bounding box center for manual layout contexts to properly center viewport
    const boundingBoxCenter = hasManualLayout ? calculateNodesBoundingBoxCenter(contextNodes) : null;

    // Determine the viewport to use:
    // 1. Use cached viewport if available (only set for manual layout)
    // 2. If navigating between manual layout levels, preserve current viewport to avoid jumps
    // 3. Otherwise, use initial viewport based on layout type (resets for autolayout)
    const viewport = cachedViewport ||
                    (isNavigatingBetweenLevels ? currentViewport : getInitialViewport(needsCenteringOffset, boundingBoxCenter));

    // Cache the viewport for manual layout contexts so we can return to it later
    if (hasManualLayout && cacheKey !== null && !cachedViewport) {
      viewportCache.set(cacheKey, viewport);
    }

    set({
      currentContext: contextId,
      contextPath,
      viewport
    });
  },

  setFilters: (nodeTypes: string[], connectionTypes: string[]) => {
    set({
      showNodeTypes: nodeTypes,
      showConnectionTypes: connectionTypes
    });
  },

  setAutoLayoutType: (type: 'auto' | 'hierarchical' | 'ring' | 'dragonfly') => {
    set({ autoLayoutType: type });
  },

  setRenderedPositions: (positions: Map<string, { x: number; y: number }>) => {
    set({ renderedPositions: positions });
  },

  setHighlightFixedNodes: (highlight: boolean) => {
    set({ highlightFixedNodes: highlight });
  },

  setSimulationRunning: (isRunning: boolean) => {
    set({ isSimulationRunning: isRunning });
  },

  // Old setLayoutMode function - no longer used but kept temporarily for compatibility
  setLayoutMode: (mode: 'auto' | 'manual' | null) => {
    const { infrastructure, nodes, currentContext, showNodeTypes, renderedPositions } = get();

    // Calculate nodes that are currently visible in the active layer/context
    const getVisibleNodesForCurrentLayer = (): VisualizationNode[] => {
      // Match VisualizationEngine visibility rules (no rack nodes rendered)
      let visible = nodes.filter(node => showNodeTypes.includes(node.type) && node.type !== 'rack');

      if (currentContext) {
        const contextNode = nodes.find(n => n.id === currentContext);
        const isNetworkContext = contextNode?.type === 'network';

        visible = visible.filter(node => {
          if (node.parent === currentContext) {
            return true;
          }

          if (isNetworkContext && node.type === 'device') {
            const rackNode = nodes.find(n => n.id === node.parent);
            return rackNode?.parent === currentContext;
          }

          return false;
        });
      } else {
        // At root, show only top-level sites (same as visualization)
        visible = visible.filter(node => node.type === 'site' || !node.parent);
      }

      return visible;
    };

    // When switching to auto mode, clear fixed coordinates from infrastructure
    if (mode === 'auto' && infrastructure && 'instances' in infrastructure) {
      const infragraph = infrastructure as InfragraphInfrastructure;
      const contextNodes = getVisibleNodesForCurrentLayer();

      // Check if any nodes have fixed coordinates that need clearing
      const hasFixedCoordinates = contextNodes.some(n => n.fx !== undefined || n.fy !== undefined);

      if (hasFixedCoordinates && contextNodes.length > 0) {
        // Deep clone infrastructure to avoid mutating the original
        const updatedInfrastructure = JSON.parse(JSON.stringify(infragraph));

        // Group nodes by instance to clear their layout settings
        const nodesByInstance = new Map<string, VisualizationNode[]>();
        contextNodes.forEach(node => {
          const instanceName = node.type === 'component'
            ? node.metadata?.instanceBaseName || node.id.split('.')[0]
            : node.id.split('.')[0];

          if (!nodesByInstance.has(instanceName)) {
            nodesByInstance.set(instanceName, []);
          }
          nodesByInstance.get(instanceName)!.push(node);
        });

        // Clear layout settings for each instance
        nodesByInstance.forEach((instanceNodes, instanceName) => {
          const instance = updatedInfrastructure.instances.find((inst: any) => inst.name === instanceName);
          if (!instance) return;

          // For component nodes, clear ALL component layouts in the device
          const componentNodes = instanceNodes.filter(n => n.type === 'component');
          if (componentNodes.length > 0 && instance.device) {
            const device = updatedInfrastructure.devices.find((d: any) => d.name === instance.device);
            if (device?.components) {
              // Clear layout for ALL components in this device, not just visible ones
              device.components.forEach((component: any) => {
                if (component.layout) {
                  delete component.layout.x_coordinates;
                  delete component.layout.y_coordinates;
                  delete component.layout.x;
                  delete component.layout.y;
                  delete component.layout.fixed;
                }
              });
            }
          }

          // Always clear instance layout as well
          if (instance.layout) {
            delete instance.layout.x;
            delete instance.layout.y;
            delete instance.layout.x_coordinates;
            delete instance.layout.y_coordinates;
            delete instance.layout.fixed;
          }
        });

        // Save current context and viewport before reload
        const savedContext = get().currentContext;
        const savedViewport = get().viewport;

        // Reload infrastructure to remove the fixed coordinates
        get().loadInfrastructure(updatedInfrastructure);

        // Restore the context to prevent unwanted navigation
        if (savedContext) {
          // Update the viewport cache to preserve the viewport through setContext
          const cacheKey = savedContext;
          const newCache = new Map(get().viewportCache);
          newCache.set(cacheKey, savedViewport);
          set({ viewportCache: newCache });

          // Now setContext will use the cached viewport
          get().setContext(savedContext);
        }
      }
    }

    // When switching to manual mode, capture current positions if they don't exist
    if (mode === 'manual' && infrastructure && 'instances' in infrastructure) {
      const infragraph = infrastructure as InfragraphInfrastructure;

      // Get visible nodes in current context/layer
      const contextNodes = getVisibleNodesForCurrentLayer();

      // Prefer latest rendered positions from the canvas (already contains autolayout)
      const positionsSource = renderedPositions.size > 0
        ? renderedPositions
        : new Map(nodes.map(n => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }]));

      const getNodePosition = (node: VisualizationNode) => {
        const pos = positionsSource.get(node.id);
        return { x: pos?.x ?? node.x ?? 0, y: pos?.y ?? node.y ?? 0 };
      };

      // Check if nodes already have manual layout coordinates
      // For hybrid mode: only capture and fix nodes that don't have YAML coordinates
      // if user has explicitly dragged them (not on mode switch)
      const missingManualCoordinates = contextNodes.some(n => n.fx === undefined || n.fy === undefined);

      // HYBRID MODE: Only capture positions for nodes that have fx/fy set
      // (meaning they were explicitly positioned, not just fallback to grid)
      // This allows nodes without YAML coordinates to use force simulation
      const nodesToCapture = contextNodes.filter(n => n.fx !== undefined && n.fy !== undefined);

      if (missingManualCoordinates && nodesToCapture.length > 0) {
        // Capture current positions for nodes that are already fixed
        // Leave nodes without coordinates to use force simulation (hybrid mode)
        // Deep clone infrastructure to avoid mutating the original
        const updatedInfrastructure = JSON.parse(JSON.stringify(infragraph));

        // Group nodes by instance (only nodes that need capturing)
        const nodesByInstance = new Map<string, VisualizationNode[]>();
        nodesToCapture.forEach(node => {
          const instanceName = node.type === 'component'
            ? node.metadata?.instanceBaseName || node.id.split('.')[0]
            : node.id.split('.')[0];

          if (!nodesByInstance.has(instanceName)) {
            nodesByInstance.set(instanceName, []);
          }
          nodesByInstance.get(instanceName)!.push(node);
        });

        // Update each instance with captured positions
        nodesByInstance.forEach((instanceNodes, instanceName) => {
          const instance = updatedInfrastructure.instances.find((inst: any) => inst.name === instanceName);
          if (!instance) return;

          // For component nodes, update component layout
          const componentNodes = instanceNodes.filter(n => n.type === 'component');
          if (componentNodes.length > 0 && instance.device) {
            const device = updatedInfrastructure.devices.find((d: any) => d.name === instance.device);
            if (device?.components) {
              // Group components by type
              const componentsByType = new Map<string, VisualizationNode[]>();
              componentNodes.forEach(node => {
                const compType = node.metadata?.componentBaseName || node.id.split('.').slice(-2, -1)[0];
                if (!componentsByType.has(compType)) {
                  componentsByType.set(compType, []);
                }
                componentsByType.get(compType)!.push(node);
              });

              // Update each component type with coordinates
              componentsByType.forEach((compNodes, compType) => {
                const component = device.components?.find((c: any) => c.name === compType);
                if (component) {
                  // Convert SVG coordinates to user coordinates
                  const REFERENCE_WIDTH = 2000;
                  const REFERENCE_HEIGHT = 2000;
                  const CENTER_X = REFERENCE_WIDTH / 2;
                  const CENTER_Y = REFERENCE_HEIGHT / 2;

                  const xCoords: number[] = [];
                  const yCoords: number[] = [];

                  compNodes.forEach(node => {
                    const pos = getNodePosition(node);
                    const userX = Math.round(pos.x - CENTER_X);
                    const userY = Math.round(CENTER_Y - pos.y);
                    xCoords.push(userX);
                    yCoords.push(userY);
                  });

                  // Create or update layout object
                  if (!component.layout) {
                    component.layout = {};
                  }
                  component.layout.x_coordinates = xCoords;
                  component.layout.y_coordinates = yCoords;
                  component.layout.fixed = true; // Mark as fixed so parser sets fx/fy
                  // Remove spacing if it exists
                  delete component.layout.x_spacing;
                  delete component.layout.y_spacing;
                }
              });
            }
          } else {
            // For instance nodes, update instance layout
            const REFERENCE_WIDTH = 2000;
            const REFERENCE_HEIGHT = 2000;
            const CENTER_X = REFERENCE_WIDTH / 2;
            const CENTER_Y = REFERENCE_HEIGHT / 2;

            const xCoords: number[] = [];
            const yCoords: number[] = [];

            instanceNodes.forEach(node => {
              const pos = getNodePosition(node);
              const userX = Math.round(pos.x - CENTER_X);
              const userY = Math.round(CENTER_Y - pos.y);
              xCoords.push(userX);
              yCoords.push(userY);
            });

            // Create or update layout object
            if (!instance.layout) {
              instance.layout = {};
            }

            // For count = 1, use scalar x/y. For count > 1, use coordinate arrays
            if (instance.count === 1) {
              instance.layout.x = xCoords[0];
              instance.layout.y = yCoords[0];
              instance.layout.fixed = true; // Mark as fixed so parser sets fx/fy
              // Remove array coordinates if they exist
              delete instance.layout.x_coordinates;
              delete instance.layout.y_coordinates;
            } else {
              instance.layout.x_coordinates = xCoords;
              instance.layout.y_coordinates = yCoords;
              instance.layout.fixed = true; // Mark as fixed so parser sets fx/fy
              // Remove scalar coordinates if they exist
              delete instance.layout.x;
              delete instance.layout.y;
            }

            // Remove spacing if it exists
            delete instance.layout.x_spacing;
            delete instance.layout.y_spacing;
          }
        });

        // Save current context and viewport before reload
        const savedContext = get().currentContext;
        const savedViewport = get().viewport;

        // Reload infrastructure to apply the new coordinates
        get().loadInfrastructure(updatedInfrastructure);

        // Restore the context to prevent unwanted navigation
        if (savedContext) {
          // Update the viewport cache to preserve the viewport through setContext
          const cacheKey = savedContext;
          const newCache = new Map(get().viewportCache);
          newCache.set(cacheKey, savedViewport);
          set({ viewportCache: newCache });

          // Now setContext will use the cached viewport
          get().setContext(savedContext);
        }
      }
    }
    // Mode is no longer stored - keeping function for compatibility but it does nothing now
  },

  reset: () => {
    set({
      infrastructure: null,
      nodes: [],
      links: [],
      zoomLevels: [],
      viewport: initialViewport,
      viewportCache: new Map(),
      currentContext: null,
      contextPath: [],
      selectedNode: null,
      hoveredNode: null,
      isLoading: false,
      error: null,
      showConnectionTypes: [],
      showNodeTypes: ['site', 'pod', 'network', 'rack', 'device', 'component'],
      renderedPositions: new Map(),
      highlightFixedNodes: true,
      editorVisible: false,
      editorContent: null,
      editorDirty: false,
      lastSyncedHash: null
    });
  },

  // Editor actions
  toggleEditor: () => {
    const { editorVisible } = get();
    set({ editorVisible: !editorVisible });

    // Sync content when opening
    if (!editorVisible) {
      get().syncToEditor();
    }
  },

  setEditorWidth: (width: number) => {
    set({ editorWidth: Math.max(300, Math.min(800, width)) });
  },

  setEditorFormat: (format: 'yaml' | 'json') => {
    set({ editorFormat: format });
    // Re-sync content in new format
    get().syncToEditor();
  },

  updateEditorContent: (content: string) => {
    const { lastSyncedHash } = get();
    const contentHash = hashString(content);
    const isDirty = contentHash !== lastSyncedHash;
    set({ editorContent: content, editorDirty: isDirty });
  },

  syncToEditor: () => {
    const { infrastructure, editorFormat } = get();
    if (!infrastructure) {
      set({ editorContent: '', lastSyncedHash: null, editorDirty: false });
      return;
    }

    try {
      const content = serialize(infrastructure, editorFormat);
      const contentHash = hashString(content);
      set({
        editorContent: content,
        lastSyncedHash: contentHash,
        editorDirty: false
      });
    } catch (error) {
      console.error('Failed to serialize infrastructure:', error);
      set({ error: `Failed to serialize to ${editorFormat.toUpperCase()}: ${(error as Error).message}` });
    }
  },

  applyEditorChanges: () => {
    const { editorContent, editorFormat } = get();
    if (!editorContent) return;

    try {
      // Parse content based on format
      const parsedData = parse(editorContent, editorFormat);

      // Apply via loadInfrastructure
      get().loadInfrastructure(parsedData);

      // Re-sync to update hash
      get().syncToEditor();
    } catch (error) {
      console.error('Failed to apply editor changes:', error);
      set({ error: `Failed to parse ${editorFormat.toUpperCase()}: ${(error as Error).message}` });
    }
  },

  revertEditorChanges: () => {
    get().syncToEditor();
  },

  setEditorHighlights: (nodeTokens: string[], linkTokens: string[]) => {
    const uniqueNodeTokens = Array.from(new Set(nodeTokens));
    const uniqueLinkTokens = Array.from(new Set(linkTokens));
    set({
      editorHighlights: {
        nodeTokens: uniqueNodeTokens,
        linkTokens: uniqueLinkTokens
      }
    });
  },

  setEditorHighlightEnabled: (enabled: boolean) => {
    set({ editorHighlightEnabled: enabled });
  },

  // Update node layout coordinates in the infrastructure data
  updateNodeLayout: (nodeId: string, svgX: number, svgY: number, capturedPositions?: Map<string, {x: number, y: number}> | null) => {
    const { infrastructure, nodes, renderedPositions } = get();

    // Use captured positions if provided, otherwise fall back to last rendered snapshot, otherwise current nodes
    const positionsSource = capturedPositions
      || (renderedPositions.size > 0 ? renderedPositions : new Map(nodes.map(n => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }])));

    // Only update if we have infragraph infrastructure
    if (!infrastructure || !('instances' in infrastructure)) {
      console.warn('Cannot update layout: not using infragraph format');
      return;
    }

    const infragraph = infrastructure as InfragraphInfrastructure;

    // Find the node to get its base name
    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      console.warn('Cannot update layout: node not found', nodeId);
      return;
    }

    // Convert SVG coordinates back to user coordinates
    // Reverse the transformation from infragraphParser:
    // SVG: x = CENTER_X + userX, y = CENTER_Y - userY
    // User: x = svgX - CENTER_X, y = CENTER_Y - svgY
    const REFERENCE_WIDTH = 2000;
    const REFERENCE_HEIGHT = 2000;
    const CENTER_X = REFERENCE_WIDTH / 2;   // = 1000
    const CENTER_Y = REFERENCE_HEIGHT / 2;  // = 1000

    const userX = svgX - CENTER_X;
    const userY = CENTER_Y - svgY;

    /*console.log('Updating node layout:', {
      nodeId,
      nodeType: node.type,
      svgCoords: { x: svgX, y: svgY },
      userCoords: { x: userX, y: userY }
    });*/

    // Parse node ID to extract name and index
    const parts = nodeId.split('.');

    // Check if this is a component node
    // Component IDs: "instance.componentName.index" (e.g., "spine-01.port.0")
    // Instance IDs: "instance.index" (e.g., "spine.0")
    const isComponent = node.type === 'component';

    let draggedInstanceName: string = '';
    let draggedInstanceIndex: number = 0;
    let draggedIndex: number = 0;
    let componentBaseName: string | undefined;
    let deviceName: string | undefined;

    if (isComponent) {
      // Component: "instanceName.instanceIndex.componentName.componentIndex"
      // e.g., "server-1-1.0.gpu.0"
      // Need to find where instanceName ends by matching against infragraph.instances
      if (parts.length < 3) {
        console.warn('Invalid component node ID format:', nodeId);
        return;
      }

      draggedIndex = parseInt(parts[parts.length - 1], 10);
      componentBaseName = parts[parts.length - 2];

      // Try to match instance name by working backwards through the parts
      // The instance name could be multi-part (e.g., "server-1-1")
      let found = false;
      for (let i = parts.length - 3; i >= 0; i--) {
        const potentialInstanceName = parts.slice(0, i + 1).join('.');
        const instance = infragraph.instances.find((inst: any) => inst.name === potentialInstanceName);
        if (instance) {
          draggedInstanceName = potentialInstanceName;
          deviceName = instance.device;
          // The part after the instance name is the instance index
          if (i + 1 < parts.length - 2) {
            draggedInstanceIndex = parseInt(parts[i + 1], 10);
          }
          found = true;
          break;
        }
      }

      if (!found) {
        console.warn('Cannot find matching instance for component node:', nodeId);
        return;
      }
    } else {
      // Instance: "instance.index" or "instance"
      draggedInstanceName = parts.length > 1 ? parts[0] : nodeId;
      draggedIndex = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    }

    const roundedX = Math.round(userX);
    const roundedY = Math.round(userY);

    // Helper function to get coordinate arrays from layout or capture from autolayout
    const getCoordinateArrays = (
      layout: any,
      count: number,
      baseNodeId: string,
      isComponentNode: boolean = false
    ): { xCoords: number[]; yCoords: number[] } => {
      // If already have coordinate lists, use them
      if (layout?.x_coordinates && layout?.y_coordinates) {
        return {
          xCoords: [...layout.x_coordinates],
          yCoords: [...layout.y_coordinates]
        };
      }

      // If using spacing notation, convert to coordinate lists
      if (layout?.x_spacing !== undefined || layout?.y_spacing !== undefined) {
        const centerX = layout.x ?? 0;
        const centerY = layout.y ?? 0;
        const xSpacing = layout.x_spacing ?? 0;
        const ySpacing = layout.y_spacing ?? 0;
        const halfCount = (count - 1) / 2;

        const xCoords = [];
        const yCoords = [];
        for (let i = 0; i < count; i++) {
          const offset = i - halfCount;
          xCoords.push(Math.round(centerX + offset * xSpacing));
          yCoords.push(Math.round(centerY + offset * ySpacing));
        }
        return { xCoords, yCoords };
      }

      // Capture current positions from autolayout
      const xCoords: number[] = [];
      const yCoords: number[] = [];
      for (let i = 0; i < count; i++) {
        let nodeId: string;
        if (isComponentNode) {
          // Component node ID: "instance.componentName.index"
          nodeId = `${baseNodeId}.${i}`;
        } else {
          // Instance node ID: "instance.index" or "instance"
          nodeId = count === 1 ? baseNodeId : `${baseNodeId}.${i}`;
        }

        const nodePos = positionsSource.get(nodeId);
        if (nodePos) {
          xCoords.push(Math.round(nodePos.x - CENTER_X));
          yCoords.push(Math.round(CENTER_Y - nodePos.y));
        } else {
          xCoords.push(layout?.x ?? 0);
          yCoords.push(layout?.y ?? 0);
        }
      }
      return { xCoords, yCoords };
    };

    let updatedInfrastructure;

    if (isComponent) {
      // Update component layout in Device definition
      // We're visualizing one device instance, so just capture the components we see
      const updatedDevices = infragraph.devices.map((device: any) => {
        if (device.name !== deviceName) {
          return device;
        }

        const updatedComponents = device.components.map((component: any) => {
          const isDraggedComponent = component.name === componentBaseName;

          // If component already has fixed layout, only update if it's the dragged one
          if (component.layout?.fixed && !isDraggedComponent) {
            return component;
          }

          if (isDraggedComponent) {
            // For count = 1, use single coordinate
            if (component.count === 1) {
              return {
                ...component,
                layout: { x: roundedX, y: roundedY, fixed: true }
              };
            }

            // For count > 1, use coordinate lists
            // Capture positions from the components we're currently visualizing
            // Component node IDs format depends on instance count:
            // - If instance.count > 1: "instanceName.instanceIndex.componentName.componentIndex"
            // - If instance.count = 1: "instanceName.componentName.componentIndex"
            const draggedInstance = infragraph.instances.find((inst: any) => inst.name === draggedInstanceName);
            const instanceCount = draggedInstance?.count || 1;
            const baseNodeId = instanceCount > 1
              ? `${draggedInstanceName}.${draggedInstanceIndex}.${componentBaseName}`
              : `${draggedInstanceName}.${componentBaseName}`;

            const { xCoords, yCoords } = getCoordinateArrays(
              component.layout,
              component.count,
              baseNodeId,
              true // isComponentNode
            );

            // Ensure arrays are large enough
            while (xCoords.length <= draggedIndex) {
              xCoords.push(xCoords.length > 0 ? xCoords[xCoords.length - 1] : 0);
            }
            while (yCoords.length <= draggedIndex) {
              yCoords.push(yCoords.length > 0 ? yCoords[yCoords.length - 1] : 0);
            }

            // Update the dragged component's coordinates
            xCoords[draggedIndex] = roundedX;
            yCoords[draggedIndex] = roundedY;

            return {
              ...component,
              layout: { x_coordinates: xCoords, y_coordinates: yCoords, fixed: true }
            };
          } else {
            // For hybrid layout: only capture positions for components that already have fixed layout
            // Leave components without coordinates alone so they can use force simulation
            if (!component.layout?.fixed) {
              return component;
            }

            // Preserve autolayout for components that already have fixed coordinates
            // Build node IDs based on instance count
            const draggedInstance = infragraph.instances.find((inst: any) => inst.name === draggedInstanceName);
            const instanceCount = draggedInstance?.count || 1;

            if (component.count === 1) {
              const nodeId = instanceCount > 1
                ? `${draggedInstanceName}.${draggedInstanceIndex}.${component.name}.0`
                : `${draggedInstanceName}.${component.name}.0`;
              const nodePos = positionsSource.get(nodeId);
              if (nodePos) {
                return {
                  ...component,
                  layout: {
                    x: Math.round(nodePos.x - CENTER_X),
                    y: Math.round(CENTER_Y - nodePos.y),
                    fixed: true
                  }
                };
              }
            } else {
              const baseNodeId = instanceCount > 1
                ? `${draggedInstanceName}.${draggedInstanceIndex}.${component.name}`
                : `${draggedInstanceName}.${component.name}`;
              const { xCoords, yCoords } = getCoordinateArrays(
                component.layout,
                component.count,
                baseNodeId,
                true // isComponentNode
              );
              return {
                ...component,
                layout: { x_coordinates: xCoords, y_coordinates: yCoords, fixed: true }
              };
            }
          }
          return component;
        });

        return { ...device, components: updatedComponents };
      });

      updatedInfrastructure = { ...infragraph, devices: updatedDevices };
    } else {
      // Update instance layout
      const updatedInstances = infragraph.instances.map((instance: any) => {
        const isDraggedInstance = instance.name === draggedInstanceName;

        // If instance already has fixed layout, only update if it's the dragged one
        if (instance.layout?.fixed && !isDraggedInstance) {
          return instance;
        }

        if (isDraggedInstance) {
          // For count = 1, use single coordinate
          if (instance.count === 1) {
            return {
              ...instance,
              layout: { x: roundedX, y: roundedY, fixed: true }
            };
          }

          // For count > 1, use coordinate lists
          const { xCoords, yCoords } = getCoordinateArrays(
            instance.layout,
            instance.count,
            instance.name,
            false // isComponentNode
          );

          // Ensure arrays are large enough
          while (xCoords.length <= draggedIndex) {
            xCoords.push(xCoords.length > 0 ? xCoords[xCoords.length - 1] : 0);
          }
          while (yCoords.length <= draggedIndex) {
            yCoords.push(yCoords.length > 0 ? yCoords[yCoords.length - 1] : 0);
          }

          // Update the dragged instance's coordinates
          xCoords[draggedIndex] = roundedX;
          yCoords[draggedIndex] = roundedY;

          return {
            ...instance,
            layout: { x_coordinates: xCoords, y_coordinates: yCoords, fixed: true }
          };
        } else {
          // For hybrid layout: only capture positions for instances that already have fixed layout
          // Leave instances without coordinates alone so they can use force simulation
          if (!instance.layout?.fixed) {
            return instance;
          }

          // Preserve autolayout for instances that already have fixed coordinates
          if (instance.count === 1) {
            const nodePos = positionsSource.get(instance.name);
            if (nodePos) {
              return {
                ...instance,
                layout: {
                  x: Math.round(nodePos.x - CENTER_X),
                  y: Math.round(CENTER_Y - nodePos.y),
                  fixed: true
                }
              };
            }
          } else {
            const { xCoords, yCoords } = getCoordinateArrays(
              instance.layout,
              instance.count,
              instance.name,
              false // isComponentNode
            );
            return {
              ...instance,
              layout: { x_coordinates: xCoords, y_coordinates: yCoords, fixed: true }
            };
          }
        }
        return instance;
      });

      updatedInfrastructure = { ...infragraph, instances: updatedInstances };
    }

    // Reparse infrastructure with updated coordinates WITHOUT resetting viewport or context
    // This preserves the user's current zoom/pan state and drill-down level to avoid visual jumps
    try {
      // Preserve current state before reparsing
      const { currentContext } = get();

      const parser = new InfragraphParser();
      parser.parse(updatedInfrastructure);

      const nodes = parser.generateVisualizationNodes();
      const links = parser.generateVisualizationLinks();
      const zoomLevels = parser.getZoomLevels();

      // Rebuild context path from new nodes array if we have a current context
      let contextPath: Array<{id: string, name: string, type: string}> = [];
      if (currentContext) {
        let currentNode = nodes.find(n => n.id === currentContext);
        const path = [];

        while (currentNode) {
          const nodeForPath = currentNode;
          path.unshift({
            id: nodeForPath.id,
            name: nodeForPath.name,
            type: nodeForPath.type
          });

          if (currentNode?.parent) {
            const parentId = currentNode.parent;
            const parentNode = nodes.find(n => n.id === parentId);
            if (!parentNode) break;
            currentNode = parentNode;
          } else {
            break;
          }
        }

        contextPath = path;
      }

      // Update infrastructure, nodes, and links while preserving viewport and context
      set({
        infrastructure: updatedInfrastructure,
        nodes,
        links,
        zoomLevels,
        // Preserve current context with rebuilt path from new nodes
        currentContext,
        contextPath
      });

      // Sync to editor if it's open
      const { editorVisible, syncToEditor } = get();
      if (editorVisible) {
        syncToEditor();
      }
    } catch (error) {
      console.error('Failed to update node layout:', error);
    }
  },

  fixAllVisibleNodes: () => {
    const { infrastructure, nodes, currentContext, showNodeTypes, renderedPositions, isSimulationRunning } = get();

    if (!infrastructure) {
      console.warn('fixAllVisibleNodes: No infrastructure loaded');
      return;
    }

    if (isSimulationRunning) {
      console.warn('fixAllVisibleNodes: Cannot fix positions while simulation is running');
      return;
    }

    // Get visible nodes in current context
    const getVisibleNodesForCurrentLayer = (): VisualizationNode[] => {
      let visible = nodes.filter(node => showNodeTypes.includes(node.type) && node.type !== 'rack');

      if (currentContext) {
        const contextNode = nodes.find(n => n.id === currentContext);
        const isNetworkContext = contextNode?.type === 'network';

        visible = visible.filter(node => {
          if (node.parent === currentContext) {
            return true;
          }
          if (isNetworkContext && node.type === 'device') {
            const deviceRackParent = node.parent;
            const rackNode = nodes.find(n => n.id === deviceRackParent);
            if (rackNode && rackNode.parent === currentContext) {
              return true;
            }
          }
          return false;
        });
      } else {
        visible = visible.filter(node => node.type === 'site' || !node.parent);
      }

      return visible;
    };

    const contextNodes = getVisibleNodesForCurrentLayer();
    if (contextNodes.length === 0) {
      console.warn('fixAllVisibleNodes: No visible nodes to fix');
      return;
    }

    // Add all visible nodes' positions to the infrastructure
    contextNodes.forEach(node => {
      const pos = renderedPositions.get(node.id);
      if (pos) {
        const { updateNodeLayout } = get();
        updateNodeLayout(node.id, pos.x, pos.y, renderedPositions);
      }
    });
  },

  releaseAllVisibleNodes: () => {
    const { infrastructure, nodes, currentContext, showNodeTypes } = get();

    if (!infrastructure) {
      console.warn('releaseAllVisibleNodes: No infrastructure loaded');
      return;
    }

    // Get visible nodes in current context
    const getVisibleNodesForCurrentLayer = (): VisualizationNode[] => {
      let visible = nodes.filter(node => showNodeTypes.includes(node.type) && node.type !== 'rack');

      if (currentContext) {
        const contextNode = nodes.find(n => n.id === currentContext);
        const isNetworkContext = contextNode?.type === 'network';

        visible = visible.filter(node => {
          if (node.parent === currentContext) {
            return true;
          }
          if (isNetworkContext && node.type === 'device') {
            const deviceRackParent = node.parent;
            const rackNode = nodes.find(n => n.id === deviceRackParent);
            if (rackNode && rackNode.parent === currentContext) {
              return true;
            }
          }
          return false;
        });
      } else {
        visible = visible.filter(node => node.type === 'site' || !node.parent);
      }

      return visible;
    };

    const contextNodes = getVisibleNodesForCurrentLayer();
    if (contextNodes.length === 0) {
      console.warn('releaseAllVisibleNodes: No visible nodes to release');
      return;
    }

    // Group nodes by type (instance or component) and their base names
    const instanceNames = new Set<string>();
    const componentsByDevice = new Map<string, Set<string>>();

    contextNodes.forEach(node => {
      if (node.type === 'component') {
        // Component node - extract device name and component name
        // For component nodes, we need the device type that CONTAINS these components
        // node.id format examples:
        //   - "instance.component.index" (e.g., "spine-01.port.0")
        //   - "instance.instanceIndex.component.componentIndex" (e.g., "server-1-1.0.gpu.0" when instance has count > 1)
        const parts = node.id.split('.');

        // Find the instance base name (may include the instance index)
        // For "server-1-1.0.gpu.0", we need to find where "server-1-1" is
        // Look for the instance by trying to find one that matches the start of the ID
        let instanceName = '';
        let instance: any = null;

        // Try to match progressively longer prefixes to find the instance name
        for (let i = 1; i <= parts.length; i++) {
          const candidateName = parts.slice(0, i).join('.');
          const foundInstance = infrastructure.instances.find((inst: any) => inst.name === candidateName);
          if (foundInstance) {
            instanceName = candidateName;
            instance = foundInstance;
            break;
          }
        }

        if (instance) {
          const deviceType = instance.device;
          // Component name is the first non-numeric part after the instance name
          const componentName = node.metadata?.componentBaseName ||
            parts.find((part, idx) => idx > instanceName.split('.').length - 1 && isNaN(Number(part)));
          //console.log(`releaseAllVisibleNodes: Component node ${node.id} -> device=${deviceType}, component=${componentName}, parentInstance=${instanceName}`);
          if (deviceType && componentName) {
            if (!componentsByDevice.has(deviceType)) {
              componentsByDevice.set(deviceType, new Set());
            }
            componentsByDevice.get(deviceType)!.add(componentName);
          }
        }
      } else {
        // Instance node - extract instance name
        const instanceName = node.id.split('.')[0];
        instanceNames.add(instanceName);
        //console.log(`releaseAllVisibleNodes: Instance node ${node.id} -> instance=${instanceName}`);
      }
    });

    //console.log(`releaseAllVisibleNodes: Found ${instanceNames.size} instances and ${componentsByDevice.size} device types with components to release`);
    //console.log('Instance names:', Array.from(instanceNames));
    //console.log('Components by device:', Array.from(componentsByDevice.entries()).map(([dev, comps]) => `${dev}: [${Array.from(comps).join(', ')}]`).join('; '));

    // Remove layout from instances
    let updatedInfrastructure = { ...infrastructure };
    if (instanceNames.size > 0) {
      updatedInfrastructure.instances = infrastructure.instances.map((instance: any) => {
        if (instanceNames.has(instance.name) && instance.layout?.fixed) {
          const { layout, ...rest } = instance;
          return rest;
        }
        return instance;
      });
    }

    // Remove layout from components
    if (componentsByDevice.size > 0) {
      //console.log('releaseAllVisibleNodes: Removing layout from components...');
      updatedInfrastructure.devices = infrastructure.devices.map((device: any) => {
        const componentsToRelease = componentsByDevice.get(device.name);
        if (!componentsToRelease) return device;

        //console.log(`releaseAllVisibleNodes: Processing device ${device.name}, components to release: [${Array.from(componentsToRelease).join(', ')}]`);
        const updatedComponents = device.components.map((component: any) => {
          const shouldRelease = componentsToRelease.has(component.name);
          //console.log(`releaseAllVisibleNodes:   - Component ${component.name}: shouldRelease=${shouldRelease}, hasFixedLayout=${component.layout?.fixed}, hasLayout=${!!component.layout}`);

          if (shouldRelease && component.layout?.fixed) {
            //console.log(`releaseAllVisibleNodes:     -> Removing layout from ${component.name}`);
            const { layout, ...rest } = component;
            return rest;
          }
          return component;
        });

        return { ...device, components: updatedComponents };
      });
    }

    // Re-parse the infrastructure to regenerate nodes without fixed positions
    try {
      //console.log('releaseAllVisibleNodes: Re-parsing infrastructure...');
      const parser = new InfragraphParser();
      parser.parse(updatedInfrastructure);

      const nodes = parser.generateVisualizationNodes();
      const links = parser.generateVisualizationLinks();
      const zoomLevels = parser.getZoomLevels();

      //console.log(`releaseAllVisibleNodes: Generated ${nodes.length} nodes`);
      // Log sample of nodes to see if they have fx/fy
      const sampleNodes = nodes.slice(0, 5);
      sampleNodes.forEach(node => {
        //console.log(`releaseAllVisibleNodes: Node ${node.id}: fx=${node.fx}, fy=${node.fy}`);
      });

      set({
        infrastructure: updatedInfrastructure,
        nodes,
        links,
        zoomLevels
      });

      //console.log('releaseAllVisibleNodes: Store updated successfully');

      // Sync to editor if it's open
      const { editorVisible, syncToEditor } = get();
      if (editorVisible) {
        syncToEditor();
      }
    } catch (error) {
      console.error('Failed to release node layouts:', error);
    }
  }
}));
